import type { UserStatus } from '@/constants/Constants';
import { sizeOfPortfolio } from '@/features/Registration/constants/UnitOptions';
import { isUserSMB } from '@/utils/isUserSMB';
import { LLTypes, type User } from '@/utils/types/User';

import type { ModernGetMeResponse } from './modern-get-me.types';
import { UserPermissions } from './permissions';
import { hasUserPermission } from './permissions.util';

const toLandlordPortalExperience = (experience: string | null | undefined): LLTypes | undefined => {
  if (experience === LLTypes.SMB || experience === LLTypes.Institutional) {
    return experience;
  }

  return undefined;
};

const toSizeOfPortfolio = (portfolioSize: string | null | undefined): sizeOfPortfolio => {
  if (!portfolioSize) {
    return sizeOfPortfolio.oneToFifty;
  }

  const validValues = Object.values(sizeOfPortfolio) as string[];
  if (validValues.includes(portfolioSize)) {
    return portfolioSize as sizeOfPortfolio;
  }

  return sizeOfPortfolio.oneToFifty;
};

/**
 * Maps the flat Go GET /v1/me response into the legacy camelCase User shape consumed by the app.
 */
export const mapModernGetMeToUser = (response: ModernGetMeResponse): User => {
  const permissions = response.permissions ?? [];
  const landlordPortalExperience = toLandlordPortalExperience(
    response.company.landlord_portal_experience,
  );
  const mappedSizeOfPortfolio = toSizeOfPortfolio(response.company.self_reported_portfolio_size);
  const hasPortfolioSize = Boolean(response.company.self_reported_portfolio_size);
  const isAccountAdmin = hasUserPermission(UserPermissions.User.CanManageUsers, permissions);

  const status: UserStatus = {
    hasProperties: response.status.has_properties,
    isNewSelfServiceUser: response.status.is_new_self_service_user,
    isAccountAdmin,
  };

  return {
    id: response.user_id,
    email: response.email,
    firstName: response.first_name || undefined,
    lastName: response.last_name || undefined,
    fullName: response.full_name || undefined,
    phone: response.mobile_number || undefined,
    role: response.role,
    sizeOfPortfolio: mappedSizeOfPortfolio,
    isAccountAdmin,
    isSMB:
      landlordPortalExperience === LLTypes.SMB ||
      (hasPortfolioSize && isUserSMB(mappedSizeOfPortfolio)),
    LLType: landlordPortalExperience,
    landlordPortalExperience,
    canEnrollProperties: hasUserPermission(
      UserPermissions.Properties.CanEnrollProperty,
      permissions,
    ),
    hasUniversalPropertyAccess: response.has_universal_property_access,
    company: {
      hubspot_parent_company_id: response.company.hubspot_parent_company_id,
      is_active: response.company.is_active,
      operator_company_id: response.company.operator_company_id,
      operator_company_marketing_name: response.company.operator_company_marketing_name,
    },
    userPermissions: permissions,
    newDealsDashboardAccess: hasUserPermission(
      UserPermissions.Deal.CanAccessDealsDashboard,
      permissions,
    ),
    claimsReportAccess: hasUserPermission(UserPermissions.Claim.CanViewReport, permissions),
    status,
    partner: response.registration_referral_source ?? undefined,
    account: {
      metadata: {
        monolith_id: response.metadata.monolith_id,
        company_role: response.metadata.company_role,
      },
    },
  };
};
